/**
 * Worker types (WagePro) — pure helpers, no DB access.
 *
 * `employees.employment_type` is free TEXT at the DB layer (V286) and may be
 * NULL on every row written before worker types were surfaced. These helpers
 * are the ONE place the API decides what a stored value means, so every
 * reader (pay-run creation, the leave-entitlement engine, the contractor
 * guards) agrees:
 *
 *   - `full_time`   — employee, full statutory treatment. NULL and any
 *                     unrecognised value resolve here (no backfill needed).
 *   - `part_time`   — employee, full statutory treatment; pay is unchanged
 *                     (salary/hours already reflect part-time), and
 *                     `fixed_annual` / `monthly` leave entitlements are
 *                     pro-rated by `employees.fte_bps` (V384).
 *   - `contract`    — fixed-term employee. Still an EMPLOYEE: full statutory
 *                     treatment, exactly like `full_time`.
 *   - `contractor`  — independent, self-employed worker. Recorded for
 *                     reference only: EXCLUDED from pay runs (and therefore
 *                     statutory deductions, payslips and disbursement files),
 *                     leave and ESS. Paid through Ledger bills, where
 *                     withholding lives.
 *
 * Moved to @jumpstone/core (from apps/api) so the API, the WagePro employee
 * form, and the migration importer share one definition of worker-type
 * semantics. `apps/api/src/services/payroll/worker_type.ts` re-exports this.
 */

/** Every accepted `employees.employment_type` value, in display order. */
export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contract',
  'contractor',
] as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

/** 100% FTE, in basis points. */
export const FULL_FTE_BPS = 10_000;

function isEmploymentType(value: string): value is EmploymentType {
  return (EMPLOYMENT_TYPES as readonly string[]).includes(value);
}

/**
 * The effective worker type of a stored value. NULL/undefined (unrecorded)
 * and any value outside {@link EMPLOYMENT_TYPES} resolve to `'full_time'` —
 * the treatment every pre-existing employee has always received.
 */
export function resolveEmploymentType(
  value: string | null | undefined,
): EmploymentType {
  if (value === null || value === undefined) return 'full_time';
  return isEmploymentType(value) ? value : 'full_time';
}

/** Whether the stored value marks an independent contractor (not an employee). */
export function isContractor(value: string | null | undefined): boolean {
  return resolveEmploymentType(value) === 'contractor';
}

/**
 * The FTE (basis points) the leave engine pro-rates by. Only a part-time
 * worker is ever scaled; a NULL `fte_bps` on a part-time worker means 100%.
 * Out-of-range stored values are clamped to (0, 10000] so a corrupt row can
 * never grant more than a full-time entitlement or a negative one.
 */
export function effectiveFteBps(
  type: string | null | undefined,
  fteBps: number | null | undefined,
): number {
  if (resolveEmploymentType(type) !== 'part_time') return FULL_FTE_BPS;
  if (fteBps === null || fteBps === undefined || !Number.isInteger(fteBps)) {
    return FULL_FTE_BPS;
  }
  if (fteBps <= 0) return FULL_FTE_BPS;
  return Math.min(fteBps, FULL_FTE_BPS);
}

/**
 * Scale a non-negative integer (days ×100, minor units, …) by an FTE in basis
 * points, rounding half-up with integer arithmetic only. `fteBps` is clamped
 * to [0, 10000]; a negative `value` is scaled by magnitude and keeps its sign.
 */
export function scaleByFte(value: number, fteBps: number): number {
  const bps = Math.max(0, Math.min(FULL_FTE_BPS, Math.trunc(fteBps)));
  if (bps === FULL_FTE_BPS) return value;
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.abs(Math.trunc(value));
  const scaled = Math.floor(
    (magnitude * bps + FULL_FTE_BPS / 2) / FULL_FTE_BPS,
  );
  return scaled === 0 ? 0 : sign * scaled;
}
